'use client';

import * as React from 'react';
import {
  ChatCloseIcon,
  ChatEntry,
  ChatToggle,
  useChat,
  useMaybeLayoutContext,
  type MessageDecoder,
  type MessageEncoder,
  type MessageFormatter,
} from '@livekit/components-react';
import toast from 'react-hot-toast';
import {
  chatMessageNotifyId,
  chatMessagePreview,
  countUnreadRemoteMessages,
  remoteMessagesToNotify,
} from '@/lib/chat-unread';
import { playChatPop } from '@/lib/raise-hand-sound';
import { ru } from '@/lib/ru';

export function AgimaChat(props: {
  messageFormatter?: MessageFormatter;
  messageDecoder?: MessageDecoder;
  messageEncoder?: MessageEncoder;
  style?: React.CSSProperties;
}) {
  const { messageFormatter, messageDecoder, messageEncoder, style } = props;
  const ulRef = React.useRef<HTMLUListElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const chatOptions = React.useMemo(
    () => ({ messageDecoder, messageEncoder }),
    [messageDecoder, messageEncoder],
  );
  const { chatMessages, send, isSending } = useChat(chatOptions);
  const layoutContext = useMaybeLayoutContext();
  const lastReadMsgAt = React.useRef<number>(0);
  const toastedIds = React.useRef(new Set<string>());
  const toastReady = React.useRef(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (inputRef.current && inputRef.current.value.trim() !== '') {
      await send(inputRef.current.value);
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  }

  React.useEffect(() => {
    ulRef.current?.scrollTo({ top: ulRef.current.scrollHeight });
  }, [chatMessages]);

  React.useEffect(() => {
    if (!layoutContext) return;
    const { widget } = layoutContext;
    if (widget.state?.showChat) {
      toast.dismiss();
      if (chatMessages.length > 0) {
        lastReadMsgAt.current = chatMessages[chatMessages.length - 1]?.timestamp ?? 0;
      }
      if (widget.state.unreadMessages !== 0) {
        widget.dispatch?.({ msg: 'unread_msg', count: 0 });
      }
      return;
    }
    const unreadMessageCount = countUnreadRemoteMessages(chatMessages, lastReadMsgAt.current);
    if (widget.state?.unreadMessages !== unreadMessageCount) {
      widget.dispatch?.({ msg: 'unread_msg', count: unreadMessageCount });
    }
  }, [chatMessages, layoutContext]);

  React.useEffect(() => {
    if (!toastReady.current) {
      for (const msg of chatMessages) {
        if (!msg.from?.isLocal) {
          toastedIds.current.add(chatMessageNotifyId(msg));
        }
      }
      toastReady.current = true;
      return;
    }
    const pending = remoteMessagesToNotify(chatMessages, {
      chatOpen: !!layoutContext?.widget.state?.showChat,
      lastReadAt: lastReadMsgAt.current,
      notifiedIds: toastedIds.current,
    });
    if (pending.length === 0) return;

    const openChat = () => {
      toast.dismiss();
      layoutContext?.widget.dispatch?.({ msg: 'show_chat' });
    };

    for (const message of pending) {
      const id = chatMessageNotifyId(message);
      toastedIds.current.add(id);
      const name = message.from?.name || message.from?.identity || 'Участник';
      const preview = chatMessagePreview(message);
      toast.custom(
        () => (
          <button type="button" className="agima-chat-toast" onClick={openChat}>
            <span className="agima-chat-toast-label">{ru.newChatMessage}</span>
            <span className="agima-chat-toast-name">{name}</span>
            {preview ? <span className="agima-chat-toast-preview">{preview}</span> : null}
          </button>
        ),
        { id: `chat-${id}`, duration: 4000 },
      );
    }
    void playChatPop();
  }, [chatMessages, layoutContext]);

  return (
    <div className="lk-chat" style={style}>
      <div className="lk-chat-header">
        {ru.messages}
        {layoutContext && (
          <ChatToggle className="lk-close-button">
            <ChatCloseIcon />
          </ChatToggle>
        )}
      </div>
      <ul className="lk-list lk-chat-messages" ref={ulRef}>
        {chatMessages.map((msg, idx, allMsg) => {
          const hideName = idx >= 1 && allMsg[idx - 1].from === msg.from;
          const hideTimestamp = idx >= 1 && msg.timestamp - allMsg[idx - 1].timestamp < 60_000;
          return (
            <ChatEntry
              key={msg.id ?? idx}
              hideName={hideName}
              hideTimestamp={hideName === false ? false : hideTimestamp}
              entry={msg}
              messageFormatter={messageFormatter}
            />
          );
        })}
      </ul>
      <form className="lk-chat-form" onSubmit={handleSubmit}>
        <input
          className="lk-form-control lk-chat-form-input"
          disabled={isSending}
          ref={inputRef}
          type="text"
          placeholder={ru.messagePlaceholder}
          onInput={(ev) => ev.stopPropagation()}
          onKeyDown={(ev) => ev.stopPropagation()}
          onKeyUp={(ev) => ev.stopPropagation()}
        />
        <button type="submit" className="lk-button lk-chat-form-button" disabled={isSending}>
          {ru.send}
        </button>
      </form>
    </div>
  );
}
